import { Patient, Room, PatientStatus, StatusLog, SurgerySubStatus, SurgeryCondition, SUB_STATUS_CONFIG, STATUS_CONFIG, Hospital, HospitalBranding, HospitalPreferences, StaffUser, StaffRole, AuditEntry, HospitalGroup } from '../types';
import * as cloudflareService from './cloudflareService';

export const STORAGE_KEY_PATIENTS = 'hospitalstatus_patients';
export const STORAGE_KEY_ROOMS = 'hospitalstatus_rooms';
const STORAGE_KEY_AUTH = 'hospitalstatus_auth';
const STORAGE_KEY_HOSPITALS = 'hospitalstatus_hospitals';

const INITIAL_HOSPITALS: Hospital[] = [];

const INITIAL_ROOMS: Room[] = [];

export const subscribeToPatients = (hospitalId: string, callback: (patients: Patient[]) => void) => {
  return cloudflareService.subscribeToPatients(hospitalId, (patients) => {
    if (!Array.isArray(patients)) return;
    const allPatients = getPatients();
    const otherPatients = allPatients.filter(p => p.hospitalId !== hospitalId);
    localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify([...otherPatients, ...patients]));
    callback(patients);
  });
};

export const subscribeToRooms = (hospitalId: string, callback: (rooms: Room[]) => void) => {
  return cloudflareService.subscribeToRooms(hospitalId, (rooms) => {
    if (!Array.isArray(rooms)) return;
    const allRooms = getRooms();
    const otherRooms = allRooms.filter(r => r.hospitalId !== hospitalId);
    localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify([...otherRooms, ...rooms]));
    callback(rooms);
  });
};

export const subscribeToPatient = cloudflareService.subscribeToPatient;
export const subscribeToHospitalConfig = cloudflareService.subscribeToHospitalConfig;

export interface AuthData {
  token: string;
  hospitalName: string;
  hospitalId: string;
  groupId?: string | null;
  role: 'ADMIN' | 'STAFF' | 'FAMILY' | 'SUPER_ADMIN' | 'GROUP_ADMIN' | 'HOSPITAL_ADMIN';
  // Staff fields (only when logged in via double PIN)
  staffId?: string;
  staffName?: string;
  staffRole?: StaffRole;
  expiresAt?: number;
}

export const getAdminContext = () => {
  try {
    const raw = localStorage.getItem('hospitalstatus_admin_ctx');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const isAuthenticated = (): boolean => {
  const adminCtx = getAdminContext();
  if (adminCtx) {
    if (!adminCtx.expiresAt || Date.now() > adminCtx.expiresAt) return false;
    return true;
  }

  const auth = localStorage.getItem(STORAGE_KEY_AUTH);
  if (!auth) return false;
  try {
    const parsed = JSON.parse(auth);
    if (!parsed.expiresAt || Date.now() > parsed.expiresAt) return false;
    return !!parsed.token;
  } catch {
    return false; // Strict fallback, no unparseable sessions allowed
  }
};

export const getAuthData = (): AuthData | null => {
  const auth = localStorage.getItem(STORAGE_KEY_AUTH);
  if (auth) {
    try {
      return JSON.parse(auth);
    } catch {
      // Fallback below
    }
  }

  // Fallback for global admins who don't have a staff session
  const adminCtx = getAdminContext();
  if (adminCtx) {
      return {
          token: 'admin_session',
          hospitalName: 'Global Admin',
          hospitalId: 'ADMIN',
          role: adminCtx.role as any
      };
  }

};

export const getAuditName = (): string => {
  const auth = getAuthData();
  if (!auth) return 'Sistema';
  if (auth.staffName) return auth.staffName;
  
  if (auth.role === 'SUPER_ADMIN') return 'Super Administrador';
  if (auth.role === 'GROUP_ADMIN') return 'Director de Cadena';
  if (auth.role === 'HOSPITAL_ADMIN') return 'Administrador Local';
  
  return 'Personal Médico';
};

export const login = async (hospitalId: string, pin: string): Promise<{ success: boolean; requiresStaffPin?: boolean }> => {
  try {
    const data = await cloudflareService.dbLogin(hospitalId, pin);
    if (data.success) {
      const authData: AuthData = {
        token: data.token || 'session_active',
        hospitalName: data.hospitalName || 'Hospital Status Admin',
        hospitalId: hospitalId,
        role: data.role,
        expiresAt: Date.now() + (48 * 60 * 60 * 1000) // Bomb: 24 horas
      };
      localStorage.setItem(STORAGE_KEY_AUTH, JSON.stringify(authData));
      return { success: true, requiresStaffPin: data.requiresStaffPin || false };
    }
  } catch (e) {
    console.error("Login failed:", e);
    return { success: false };
  }
  return { success: false };
};

// Second step of double-PIN login: validates the staff's personal PIN
export const staffLogin = async (hospitalId: string, staffPin: string): Promise<boolean> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/auth/staff-login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hospitalId, staffPin })
    });
    const data = await res.json();
    if (data.success) {
      // Enrich the local auth data with staff info
      const prev = getAuthData();
      const authData: AuthData = {
        ...(prev || { token: data.token || 'session_active', hospitalName: data.hospitalName, hospitalId, role: 'STAFF' }),
        staffId: data.staffId,
        staffName: data.staffName,
        staffRole: data.staffRole,
        hospitalName: data.hospitalName || prev?.hospitalName || '',
        expiresAt: Date.now() + (48 * 60 * 60 * 1000) // Reset bomb en login interno
      };
      localStorage.setItem(STORAGE_KEY_AUTH, JSON.stringify(authData));
      return true;
    }
  } catch (e) {
    console.error("Staff login failed:", e);
  }
  return false;
};

export const logout = async () => {
  // Try to cleanly logout on backend if possible
  try { await cloudflareService.dbLogout(); } catch (e) {}
  
  // Clear all local auth state
  localStorage.removeItem(STORAGE_KEY_AUTH);
  localStorage.removeItem('hospitalstatus_admin_ctx');
};

export const resetData = () => {
  localStorage.removeItem(STORAGE_KEY_HOSPITALS);
  localStorage.removeItem(STORAGE_KEY_ROOMS);
  localStorage.removeItem(STORAGE_KEY_PATIENTS);
  localStorage.removeItem(STORAGE_KEY_AUTH);
  window.location.reload();
};

export const getRooms = (): Room[] => {
  const stored = localStorage.getItem(STORAGE_KEY_ROOMS);
  return stored ? JSON.parse(stored) : INITIAL_ROOMS;
};

// Async version for primary source
export const fetchRooms = async (hospitalId: string): Promise<Room[]> => {
  try {
    const rooms = await cloudflareService.getRooms(hospitalId);
    const allRooms = getRooms();
    const otherRooms = allRooms.filter(r => r.hospitalId !== hospitalId);
    localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify([...otherRooms, ...rooms]));
    return rooms;
  } catch (e) {
    console.error("Failed to fetch rooms from DB, using local", e);
    return getRooms().filter(r => r.hospitalId === hospitalId);
  }
};

export const getPatients = (): Patient[] => {
  const stored = localStorage.getItem(STORAGE_KEY_PATIENTS);
  return stored ? JSON.parse(stored) : [];
};

export const getPatientByCode = async (code: string): Promise<Patient | undefined> => {
  const normalizedCode = code.toUpperCase().replace(/-/g, '');
  try {
    const patient = await cloudflareService.getPatientById(normalizedCode);
    if (patient) return patient;
  } catch (e) {
    console.error("Failed to fetch patient by ID, falling back to local", e);
  }
  const patients = getPatients();
  return patients.find(p => p.id.replace(/-/g, '') === normalizedCode);
};

export const getPatientsByHospital = async (hospitalId: string): Promise<Patient[]> => {
  try {
    const patients = await cloudflareService.getPatients(hospitalId);
    const allPatients = getPatients();
    const otherPatients = allPatients.filter(p => p.hospitalId !== hospitalId);
    localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify([...otherPatients, ...patients]));
    return patients;
  } catch (e) {
    console.error("Failed to fetch patients for hospital", e);
    return getPatients().filter(p => p.hospitalId === hospitalId);
  }
};

export const admitPatient = async (roomID: string, name?: string, procedureName?: string, doctorName?: string): Promise<Patient> => {
  const auth = getAuthData();
  const hospitalId = auth?.hospitalId || 'H1';

  const newPatient: Patient = {
    id: 'TEMP_ID', // Replaced by backend
    hospitalId: hospitalId,
    name: name || '',
    procedureName: procedureName,
    doctorName: doctorName,
    room: roomID,
    currentStatus: PatientStatus.WAITING,
    currentCondition: SurgeryCondition.NORMAL,
    surgeryProgress: 0,
    admissionTime: new Date().toISOString(),
    logs: [{
      status: PatientStatus.WAITING,
      timestamp: new Date().toISOString(),
      updatedBy: getAuditName(),
    }]
  };

  // -- CLOUDFLARE SYNC PRIMARY --
  const createdPatient = await cloudflareService.dbAdmitPatient(newPatient);
  const finalPatient = createdPatient || newPatient;

  // Update local cache
  const patients = getPatients();
  const rooms = getRooms();
  const updatedRooms = rooms.map(r => r.id === roomID ? { ...r, currentPatientId: finalPatient.id } : r);
  localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify([...patients, finalPatient]));
  localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(updatedRooms));

  return finalPatient;
};

export const updatePatientStatus = async (
  patientId: string,
  newStatus: PatientStatus,
  subStatus?: SurgerySubStatus,
  condition?: SurgeryCondition
): Promise<Patient> => {
  const auth = getAuthData();
  const hospitalId = auth?.hospitalId || 'H1';

  const patients = await getPatientsByHospital(hospitalId);
  const rooms = await cloudflareService.getRooms(hospitalId);

  const patientIndex = patients.findIndex(p => p.id === patientId);
  if (patientIndex === -1) throw new Error('Patient not found');

  const updatedPatient = { ...patients[patientIndex] };
  const isStatusChange = updatedPatient.currentStatus !== newStatus;
  const isSubStatusChange = subStatus && updatedPatient.currentSubStatus !== subStatus;
  const isConditionChange = condition && updatedPatient.currentCondition !== condition;

  if (isStatusChange || isSubStatusChange || isConditionChange) {
    updatedPatient.currentStatus = newStatus;
    let newProgress = STATUS_CONFIG[newStatus]?.progress ?? 0;

    if (newStatus === PatientStatus.SURGERY) {
      if (isSubStatusChange && subStatus) {
        newProgress = SUB_STATUS_CONFIG[subStatus].baseProgress;
      } else if (!updatedPatient.currentSubStatus) {
        newProgress = SUB_STATUS_CONFIG[SurgerySubStatus.INCISION].baseProgress;
      } else {
        newProgress = updatedPatient.surgeryProgress;
      }
    }

    updatedPatient.surgeryProgress = newProgress;
    if (isSubStatusChange && subStatus) updatedPatient.currentSubStatus = subStatus;
    if (isConditionChange && condition) updatedPatient.currentCondition = condition;

    if (newStatus !== PatientStatus.SURGERY) {
      updatedPatient.currentSubStatus = undefined;
      updatedPatient.currentCondition = SurgeryCondition.NORMAL;
    } else {
      if (!updatedPatient.currentSubStatus && !subStatus) updatedPatient.currentSubStatus = SurgerySubStatus.INCISION;
      if (!updatedPatient.currentCondition && !condition) updatedPatient.currentCondition = SurgeryCondition.NORMAL;
    }

    updatedPatient.logs.push({
      status: newStatus,
      subStatus: updatedPatient.currentSubStatus,
      condition: updatedPatient.currentCondition,
      timestamp: new Date().toISOString(),
      updatedBy: getAuditName()
    });

    // -- CLOUDFLARE SYNC PRIMARY --
    // 🛡️ BUG FIX: Send ONLY status-specific fields, NOT the full patient object.
    // Sending the full object risks overwriting the `name` field with a stale or
    // empty value from the client's local state. The backend `allowedKeys` guard
    // already strips unknown fields, but this is safer and more explicit.
    const statusPayload: Partial<Patient> = {
      currentStatus: updatedPatient.currentStatus,
      surgeryProgress: updatedPatient.surgeryProgress,
      logs: updatedPatient.logs,
      ...(updatedPatient.currentSubStatus !== undefined && { currentSubStatus: updatedPatient.currentSubStatus }),
      ...(updatedPatient.currentCondition !== undefined && { currentCondition: updatedPatient.currentCondition }),
      ...(updatedPatient.dischargeTime !== undefined && { dischargeTime: updatedPatient.dischargeTime }),
    };
    await cloudflareService.dbUpdatePatient(updatedPatient.id, statusPayload);

    // Update local cache
    const localPatients = getPatients();
    const localIndex = localPatients.findIndex(p => p.id === updatedPatient.id);
    if (localIndex >= 0) {
      localPatients[localIndex] = updatedPatient;
      localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify(localPatients));
    }

    if (newStatus === PatientStatus.EXIT || newStatus === PatientStatus.RECOVERY) {
      if (newStatus === PatientStatus.EXIT) {
        updatedPatient.dischargeTime = new Date().toISOString();
      }

      const localRooms = getRooms();
      const updatedRooms = localRooms.map(r => r.id === updatedPatient.room ? { ...r, currentPatientId: null } : r);
      localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(updatedRooms));
      await cloudflareService.dbClearRoom(updatedPatient.hospitalId, updatedPatient.room);
    }
  }

  return updatedPatient;
};

export const incrementSurgeryProgress = async (patientId: string): Promise<Patient> => {
  const auth = getAuthData();
  const hospitalId = auth?.hospitalId || 'H1';

  const patients = await getPatientsByHospital(hospitalId);
  const patientIndex = patients.findIndex(p => p.id === patientId);
  if (patientIndex === -1) throw new Error('Patient not found');

  const patient = patients[patientIndex];
  if (patient.currentStatus === PatientStatus.SURGERY &&
    patient.currentSubStatus === SurgerySubStatus.PROCEDURE) {

    const newProgress = Math.min(patient.surgeryProgress + 5, 80);

    if (newProgress !== patient.surgeryProgress) {
      patient.surgeryProgress = newProgress;
      patient.logs.push({
        status: PatientStatus.SURGERY,
        subStatus: SurgerySubStatus.PROCEDURE,
        condition: patient.currentCondition,
        timestamp: new Date().toISOString(),
        updatedBy: getAuditName()
      });

      // 🛡️ BUG FIX: Send ONLY progress-specific fields, never the full patient object.
      await cloudflareService.dbUpdatePatient(patient.id, {
        surgeryProgress: patient.surgeryProgress,
        logs: patient.logs,
      });
      const localPatients = getPatients();
      const localIndex = localPatients.findIndex(p => p.id === patientId);
      if (localIndex >= 0) {
        localPatients[localIndex] = patient;
        localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify(localPatients));
      }
    }
  }
  return patient;
};

export const updatePatientDetails = async (patientId: string, updates: Partial<Patient>): Promise<Patient> => {
  const auth = getAuthData();
  const hospitalId = auth?.hospitalId || 'H1';

  const patients = await getPatientsByHospital(hospitalId);
  const patientIndex = patients.findIndex(p => p.id === patientId);
  if (patientIndex === -1) throw new Error('Patient not found');

  const patient = patients[patientIndex];

  // Set lastEditedAt timestamp
  const finalUpdates = {
    ...updates,
    lastEditedAt: new Date().toISOString()
  };

  // Sync with backend
  await cloudflareService.dbUpdatePatient(patient.id, finalUpdates);

  // Update local cache
  const updatedPatient = { ...patient, ...finalUpdates };
  const localPatients = getPatients();
  const localIndex = localPatients.findIndex(p => p.id === patient.id);
  if (localIndex >= 0) {
    localPatients[localIndex] = updatedPatient;
    localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify(localPatients));
  }

  // If room was changed, handle it
  if (updates.room && updates.room !== patient.room) {
    const rooms = getRooms();
    const updatedRooms = rooms.map(r => {
      if (r.id === patient.room) return { ...r, currentPatientId: null };
      if (r.id === updates.room) return { ...r, currentPatientId: patient.id };
      return r;
    });
    localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(updatedRooms));
  }

  return updatedPatient;
};

export const deletePatient = async (patientId: string): Promise<void> => {
  const auth = getAuthData();
  const hospitalId = auth?.hospitalId || 'H1';

  const patients = await getPatientsByHospital(hospitalId);
  const patient = patients.find(p => p.id === patientId);

  // Always call the API — even if the patient isn't in the active list,
  // they may exist only in surgery_history (archived). The backend
  // handles both active and archived deletions gracefully.
  await cloudflareService.dbDeletePatient(patientId);

  // Update local cache for patients (if they were active)
  const localPatients = getPatients();
  const filteredPatients = localPatients.filter(p => p.id !== patientId);
  localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify(filteredPatients));

  // Update local cache for rooms if assigned
  if (patient?.room && patient.currentStatus !== PatientStatus.EXIT) {
    const rooms = getRooms();
    const updatedRooms = rooms.map(r =>
      r.id === patient.room ? { ...r, currentPatientId: null } : r
    );
    localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(updatedRooms));
  }
};

export const deletePatientGlobal = async (patientId: string): Promise<void> => {
  await cloudflareService.dbDeletePatient(patientId);
};

export const changePatientRoom = async (patientId: string, newRoomId: string): Promise<Patient> => {
  const auth = getAuthData();
  const hospitalId = auth?.hospitalId || 'H1';

  const patients = await getPatientsByHospital(hospitalId);
  const patientIndex = patients.findIndex(p => p.id === patientId);
  if (patientIndex === -1) throw new Error('Patient not found');

  const oldRoomId = patients[patientIndex].room;
  const patient = { ...patients[patientIndex], room: newRoomId };

  // Sync with backend
  await cloudflareService.dbUpdatePatient(patient.id, { room: newRoomId });

  // Update local cache
  const localPatients = getPatients();
  const localIndex = localPatients.findIndex(p => p.id === patient.id);
  if (localIndex >= 0) {
    localPatients[localIndex] = patient;
    localStorage.setItem(STORAGE_KEY_PATIENTS, JSON.stringify(localPatients));
  }

  // Update rooms cache locally as well
  const rooms = getRooms();
  const updatedRooms = rooms.map(r => {
    if (r.id === oldRoomId) return { ...r, currentPatientId: null };
    if (r.id === newRoomId) return { ...r, currentPatientId: patient.id };
    return r;
  });
  localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(updatedRooms));

  return patient;
};



// -- HOSPITAL MANAGEMENT --

export const getHospitals = async (): Promise<Hospital[]> => {
  try {
    const hospitals = await cloudflareService.getHospitals();
    // Polyfill handles for legacy data
    const patched = hospitals.map(h => {
      if (!h.handle) {
        const base = h.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        return { ...h, handle: base };
      }
      return h;
    });
    localStorage.setItem(STORAGE_KEY_HOSPITALS, JSON.stringify(patched));
    return patched;
  } catch (e) {
    console.error("Failed to fetch hospitals from DB, using local", e);
    const stored = localStorage.getItem(STORAGE_KEY_HOSPITALS);
    const hospitals: Hospital[] = stored ? JSON.parse(stored) : INITIAL_HOSPITALS;
    // Polyfill here too
    return hospitals.map(h => {
      if (!h.handle) {
        const base = h.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        return { ...h, handle: base };
      }
      return h;
    });
  }
};

export const getHospitalById = async (id: string): Promise<Hospital | undefined> => {
  try {
    const hospital = await cloudflareService.getHospitalById(id);
    return hospital || undefined;
  } catch (e) {
    console.error("Failed to fetch hospital by ID from DB", e);
    const stored = localStorage.getItem(STORAGE_KEY_HOSPITALS);
    const hospitals: Hospital[] = stored ? JSON.parse(stored) : INITIAL_HOSPITALS;
    return hospitals.find(h => h.id === id);
  }
};

export const getHospitalByHandle = async (handle: string): Promise<Hospital | undefined> => {
  const hospitals = await getHospitals();
  return hospitals.find(h => h.handle === handle);
};

export const getHospitalGroups = async (): Promise<HospitalGroup[]> => {
  return await cloudflareService.getHospitalGroups();
};

export const deleteHospitalGroup = async (id: string) => {
  await cloudflareService.dbDeleteHospitalGroup(id);
};

export const saveHospitalGroup = async (group: Partial<HospitalGroup>, forceCreate: boolean = false) => {
  const finalGroup = { ...group };
  if (!finalGroup.id && forceCreate) {
    finalGroup.id = `G${Date.now()}`;
  }
  await cloudflareService.dbSaveHospitalGroup(finalGroup, forceCreate);
};

export const saveHospital = async (hospital: Hospital): Promise<void> => {
  await cloudflareService.dbSaveHospital(hospital);

  // Update local cache
  const stored = localStorage.getItem(STORAGE_KEY_HOSPITALS);
  const hospitals: Hospital[] = stored ? JSON.parse(stored) : INITIAL_HOSPITALS;
  const index = hospitals.findIndex(h => h.id === hospital.id);

  if (index >= 0) {
    hospitals[index] = hospital;
  } else {
    hospitals.push(hospital);
  }
  localStorage.setItem(STORAGE_KEY_HOSPITALS, JSON.stringify(hospitals));
};

export const getRoomsByHospital = async (hospitalId: string): Promise<Room[]> => {
  return fetchRooms(hospitalId);
};

export const addRoom = async (hospitalId: string, name: string): Promise<Room> => {
  const newRoom: Room = {
    id: `${hospitalId}-R${Date.now()}`,
    hospitalId,
    name,
    currentPatientId: null
  };

  await cloudflareService.dbAddRoom(newRoom);

  const rooms = getRooms();
  rooms.push(newRoom);
  localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(rooms));
  return newRoom;
};

export const updateRoomName = async (roomId: string, name: string): Promise<void> => {
  await cloudflareService.dbUpdateRoom(roomId, name);

  const rooms = getRooms();
  const index = rooms.findIndex(r => r.id === roomId);
  if (index !== -1) {
    rooms[index].name = name;
    localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(rooms));
  }
};

export const deleteRoom = async (roomId: string): Promise<void> => {
  await cloudflareService.dbDeleteRoom(roomId);

  const rooms = getRooms();
  const filtered = rooms.filter(r => r.id !== roomId);
  localStorage.setItem(STORAGE_KEY_ROOMS, JSON.stringify(filtered));
};


export const createHospital = async (name: string, maxRooms: number, customHandle?: string, initialData?: Partial<Hospital>): Promise<Hospital> => {
  // Generate a unique ID using timestamp to avoid collisions with DB
  const newId = `H${Date.now()}`;

  let handle = customHandle;
  if (!handle) {
    handle = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  const staffPin = Math.floor(1000 + Math.random() * 9000).toString();

  const newHospital: Hospital = {
    id: newId,
    name,
    handle,
    maxRooms,
    branding: {
      primaryColor: '#5B65DC',
      secondaryColor: '#122056',
      backgroundColor: '#f2f2f7',
      ...(initialData?.branding || {})
    },
    totalPatients: 0,
    createdAt: new Date().toISOString(),
    staffPin,
    isActive: true,
    preferences: {
      autoDeleteHours: 3,
      allowFamilyNotifications: true,
      showProcedureName: true,
      showPatientName: true,
      ...(initialData?.preferences || {})
    },
    ...initialData // Merge any other top-level overrides (contact, address, etc through initialData filtering if needed, but explicit spread is safer for known types)
  };

  // Ensure critical fields aren't overwritten by initialData spread if it contained them (though unlikely from caller)
  newHospital.id = newId;
  newHospital.handle = handle; // Ensure handle is respected

  // Use forceCreate to ensure POST is used for new hospitals
  await cloudflareService.dbSaveHospital(newHospital, true);

  // Update local cache
  const stored = localStorage.getItem(STORAGE_KEY_HOSPITALS);
  const hospitals: Hospital[] = stored ? JSON.parse(stored) : INITIAL_HOSPITALS;
  hospitals.push(newHospital);
  localStorage.setItem(STORAGE_KEY_HOSPITALS, JSON.stringify(hospitals));

  // Generate rooms for this hospital
  const roomNames = [];
  for (let i = 1; i <= maxRooms; i++) {
    roomNames.push(`Quirófano ${i}`);
  }
  await cloudflareService.initializeHospitalRooms(newHospital.id, roomNames);

  // Refresh local cache for rooms
  await fetchRooms(newHospital.id);

  return newHospital;
};

export const updateHospitalBranding = async (hospitalId: string, branding: HospitalBranding): Promise<void> => {
  const hospital = await getHospitalById(hospitalId);
  if (hospital) {
    hospital.branding = branding;
    await saveHospital(hospital);
  }
};

export const updateHospitalHandle = async (hospitalId: string, newHandle: string): Promise<boolean> => {
  const hospitals = await getHospitals();
  const exists = hospitals.some(h => h.handle === newHandle && h.id !== hospitalId);
  if (exists) return false;

  const hospital = hospitals.find(h => h.id === hospitalId);
  if (hospital) {
    hospital.handle = newHandle;
    await saveHospital(hospital);
    return true;
  }
  return false;
};

export const updateHospitalGroup = async (hospitalId: string, groupId: string | null): Promise<void> => {
  const hospital = await getHospitalById(hospitalId);
  if (hospital) {
    hospital.groupId = groupId;
    await saveHospital(hospital);
  }
};

export const deleteHospital = async (id: string): Promise<void> => {
  await cloudflareService.dbDeleteHospital(id);

  const stored = localStorage.getItem(STORAGE_KEY_HOSPITALS);
  if (stored) {
    const hospitals: Hospital[] = JSON.parse(stored);
    const updatedHospitals = hospitals.filter(h => h.id !== id);
    localStorage.setItem(STORAGE_KEY_HOSPITALS, JSON.stringify(updatedHospitals));
  }
};

export const getAllPatientsHistory = async (): Promise<Patient[]> => {
  try {
    return await cloudflareService.getAllPatientsHistory();
  } catch (e: any) {
    if (e.message === 'UNAUTHORIZED') throw e;
    console.error("Failed to fetch global history", e);
    return getPatients(); // Fallback to local
  }
};

export const getHospitalAnalytics = async (hospitalId: string, startDate?: string, endDate?: string): Promise<any> => {
  return await cloudflareService.fetchHospitalAnalytics(hospitalId, startDate, endDate);
};

export const getHospitalHistory = async (hospitalId: string): Promise<Patient[]> => {
  try {
    return await cloudflareService.getHospitalHistory(hospitalId);
  } catch (e) {
    console.error("Failed to fetch hospital history", e);
    return [];
  }
};

export const getAvailableRooms = async (hospitalId: string): Promise<Room[]> => {
    return (await apiFetch(`/hospitals/${hospitalId}/rooms/available`)) || [];
};

// ============================================================
// SNAPSHOTS (Historical Resumen)
// ============================================================
export const getHospitalSnapshotDates = async (hospitalId: string): Promise<string[]> => {
    return (await apiFetch(`/hospitals/${hospitalId}/snapshots`)) || [];
};

export const getHospitalSnapshot = async (hospitalId: string, date: string): Promise<any> => {
    return (await apiFetch(`/hospitals/${hospitalId}/snapshots?date=${date}`));
};

// ============================================================
// 👥 STAFF USER MANAGEMENT
// ============================================================

export const getStaffByHospital = async (hospitalId: string): Promise<StaffUser[]> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/hospitals/${hospitalId}/staff`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch staff');
    return await res.json();
  } catch (e) {
    console.error('Failed to fetch staff:', e);
    return [];
  }
};

export const createStaffUser = async (hospitalId: string, data: { name: string; role: StaffRole; pin: string }): Promise<{ success: boolean; staffId?: string; error?: string }> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/hospitals/${hospitalId}/staff`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await res.json();
    if (!res.ok && !result.error) {
      result.error = `Error del servidor (${res.status})`;
      result.success = false;
    }
    return result;
  } catch (e) {
    return { success: false, error: 'Error de red. Verifica tu conexión.' };
  }
};

export const getStaffByGroup = async (groupId: string): Promise<StaffUser[]> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/groups/${groupId}/staff`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch group staff');
    return await res.json();
  } catch (e) {
    console.error('Failed to fetch group staff:', e);
    return [];
  }
};

export const createGroupStaff = async (groupId: string, data: { name: string; email?: string; role: StaffRole; pin?: string }): Promise<{ success: boolean; staffId?: string; error?: string }> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/groups/${groupId}/staff`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await res.json();
    if (!res.ok && !result.error) {
      result.error = `Error del servidor (${res.status})`;
      result.success = false;
    }
    return result;
  } catch (e) {
    console.error('Failed to create group staff:', e);
    return { success: false, error: 'Error de red. Verifica tu conexión.' };
  }
};

export const updateStaffUser = async (staffId: string, data: Partial<{ name: string; role: StaffRole; pin: string; isActive: boolean }>): Promise<boolean> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/staff/${staffId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await res.json();
    return result.success;
  } catch (e) {
    console.error('Failed to update staff:', e);
    return false;
  }
};

export const deleteStaffUser = async (staffId: string): Promise<boolean> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/staff/${staffId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    const result = await res.json();
    return result.success;
  } catch (e) {
    console.error('Failed to delete staff:', e);
    return false;
  }
};

export const getAuditLog = async (hospitalId: string): Promise<AuditEntry[]> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/hospitals/${hospitalId}/audit`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch audit log');
    return await res.json();
  } catch (e) {
    console.error('Failed to fetch audit log:', e);
    return [];
  }
};

// Reveal the plaintext master PIN (admin-only endpoint)
export const revealHospitalPin = async (hospitalId: string): Promise<string | null> => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/hospitals/${hospitalId}/reveal-pin`, {
      credentials: 'include'
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.pin || null;
  } catch (e) {
    console.error('Failed to reveal PIN:', e);
    return null;
  }
};

// ============================================================
// 📺 SCREEN SESSION MANAGEMENT
// ============================================================

export const getScreenSessions = async (hospitalId?: string) => {
  return cloudflareService.getScreenSessions(hospitalId);
};

export const deleteScreenSession = async (screenId: string) => {
  return await cloudflareService.deleteScreenSession(screenId);
};

export const requestScreenPairing = cloudflareService.requestScreenPairing;
export const checkScreenPairingStatus = cloudflareService.checkScreenPairingStatus;
export const approveScreenPairing = cloudflareService.approveScreenPairing;

export const sendScreenHeartbeat = cloudflareService.sendScreenHeartbeat;

export const updateScreenSession = cloudflareService.updateScreenSession;

export const subscribeToScreenConfig = cloudflareService.subscribeToScreenConfig;

// ============================================================
// 🔐 DEVELOPER PORTAL TOKENS
// ============================================================

export const getDocTokens = async () => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/docs/tokens`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch tokens');
    return await res.json();
  } catch (e) {
    console.error('Failed to fetch doc tokens:', e);
    return [];
  }
};

export const createDocToken = async (label: string, hospitalId?: string) => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/docs/tokens`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label, hospitalId })
    });
    return await res.json();
  } catch (e) {
    return { success: false, error: 'Network error' };
  }
};

export const revokeDocToken = async (tokenId: string) => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/docs/tokens/${tokenId}/revoke`, {
      method: 'PUT',
      credentials: 'include'
    });
    return await res.json();
  } catch (e) {
    return { success: false };
  }
};

export const restoreDocToken = async (tokenId: string) => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/docs/tokens/${tokenId}/restore`, {
      method: 'PUT',
      credentials: 'include'
    });
    return await res.json();
  } catch (e) {
    return { success: false };
  }
};

export const deleteDocToken = async (tokenId: string) => {
  try {
    const API_BASE = import.meta.env.VITE_API_BASE || '';
    const res = await cloudflareService.apiFetch(`${API_BASE}/api/docs/tokens/${tokenId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    return await res.json();
  } catch (e) {
    return { success: false };
  }
};

// ============================================================
// 👥 STAFF SESSIONS (Active Sessions Dashboard)
// ============================================================

export const getStaffSessions2 = async (hospitalId?: string): Promise<any[]> => {
  return cloudflareService.getStaffSessions(hospitalId);
};

export const revokeStaffSession = async (sessionId: string): Promise<boolean> => {
  return cloudflareService.revokeStaffSession(sessionId);
};
